import "./support-tab-storage";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { type BackButtonEvent, IonApp, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { SUPPORT_SESSION_PATH } from "@vidimo/shared";
import { ThemeProvider } from "next-themes";
import { Suspense, useEffect } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import { ErrorBoundary } from "@/components/error-boundary";
import { SystemThemeSync } from "@/components/system-theme-sync";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { PickerSheetProvider } from "@/components/ui/picker-sheet";
import { InterfaceLocaleRoot } from "@/i18n/interface-locale-root";
import { seedInterfaceLocale } from "@/i18n/locale-store";
import { carryNativeSessionTokenOver } from "@/lib/auth-client";
import {
  getCapgoStatus,
  isDevMenuVisible,
  registerCapgoListeners,
} from "@/lib/capgo";
import { useLaunchSplashRelease } from "@/lib/launch-splash";
import { lazyChunk } from "@/lib/lazy-chunk";
import { useAppSession } from "@/lib/offline-session";
import { createQueryClient } from "@/lib/query-client";
import { initSentry, useSentryIdentity } from "@/lib/sentry";
import { useIsSigningOut } from "@/lib/sign-out-state";
import { leaveSupportSession, SUPPORT_SESSION } from "@/lib/support-session";
import { ConnectApp } from "./connect/connect-app";
import { isConnectPagePath, misplacedPageUrl } from "./connect/origins";
import { App as ShellApp } from "./shell/app";
import { SupportSessionPage } from "./shell/support-session-page";
import "@ionic/react/css/core.css";
import "@ionic/react/css/structure.css";
import "../styles/globals.css";

initSentry();

// A staging/production redeploy rewrites Vite's chunk hashes, so an already-open
// tab that lazy-loads a chunk after the deploy gets a 404 and Vite fires
// `vite:preloadError` instead of an uncaught "Failed to fetch dynamically
// imported module". Reload once to pick up the current bundle; the debounce
// prevents an infinite reload loop when the chunk is genuinely unreachable
// (offline, a real 404).
//
// Cancelling the event makes Vite resolve the `import()` with `undefined`
// rather than reject it, which is why every code-split screen goes through
// `lazyChunk` (`@/lib/lazy-chunk`): plain `React.lazy` crashes on that value.
const PRELOAD_RELOAD_TS_KEY = "vite:preloadError:lastReloadTs";
const PRELOAD_RELOAD_DEBOUNCE_MS = 10_000;

/**
 * How long after a navigation starts a preload failure is attributed to it.
 *
 * Leaving the page aborts every chunk still in flight, and an aborted
 * `import()` rejects exactly like one whose URL is gone — same event, same
 * error. Reloading on that would cancel the navigation that caused it and drop
 * the reader back on the page they were leaving, which breaks the hard handoffs
 * the app makes on purpose (the OAuth authorize resume, the Stripe checkout
 * redirect) and turns any external link into a bounce.
 *
 * `beforeunload` is the signal to read: it fires when the navigation starts,
 * ahead of the aborts (measured at 2-5ms), where `pagehide` and `unload` only
 * arrive once the next document has already committed. A window rather than a
 * sticky flag, so a navigation that never completes — a download link, a
 * cancelled handoff — restores chunk recovery instead of disabling it for the
 * life of the document.
 */
const NAVIGATION_ABORT_WINDOW_MS = 2000;
let navigationStartedAt = 0;

function registerPreloadErrorAutoReload() {
  window.addEventListener("beforeunload", () => {
    navigationStartedAt = Date.now();
  });
  window.addEventListener("vite:preloadError", (event) => {
    const abortedByNavigation =
      Date.now() - navigationStartedAt < NAVIGATION_ABORT_WINDOW_MS;
    if (abortedByNavigation) {
      // The chunk was aborted by the navigation, not missing. Swallow it —
      // this document is on its way out either way.
      event.preventDefault();
      return;
    }
    const lastReloadTs = Number(
      sessionStorage.getItem(PRELOAD_RELOAD_TS_KEY) ?? 0
    );
    const reloadedRecently =
      Date.now() - lastReloadTs < PRELOAD_RELOAD_DEBOUNCE_MS;
    if (reloadedRecently) {
      // Already reloaded within the window: let the error surface instead of
      // looping on a chunk that is truly gone.
      return;
    }
    sessionStorage.setItem(PRELOAD_RELOAD_TS_KEY, String(Date.now()));
    event.preventDefault();
    window.location.reload();
  });
}

registerPreloadErrorAutoReload();

setupIonicReact({
  mode: "ios",
});

const queryClient = createQueryClient();

// Heavy chunk: PowerSync SQLite WASM init + Twilio native plugin +
// collections. Loading it eagerly at boot blocks the login screen's text
// input handling for 5-10s while the WASM DB initialises on the main
// thread. Lazy import + session gate keeps the pre-login bundle tiny.
const AuthenticatedShell = lazyChunk(
  () => import("./shell/authenticated-shell")
);

/**
 * The channel connect pages read nothing synced and hold no session: they run
 * on the connect origin (`connect/origins.ts`) under their own router, which
 * mounts nothing that asks the API for one. Read once per load: they leave
 * through a full page load (`connect/hand-back.ts`), which lands on a path
 * that mounts the shell.
 */
const IS_CONNECT_PAGE = isConnectPagePath(window.location.pathname);

function ShellAppBoundary() {
  return IS_CONNECT_PAGE ? <ConnectApp /> : <SessionShellBoundary />;
}

function SessionShellBoundary() {
  const { data: session, error, isPending } = useAppSession();
  const isLoggedIn = !!session?.user.id;
  useSentryIdentity(session);
  const isSigningOut = useIsSigningOut();
  useHoldAppWhileSigningOut(isSigningOut);
  useEndSupportTabWithItsSession(!(isPending || isLoggedIn || error));

  // The session is gone but the device is still being cleared: hold here
  // rather than show the sign-in form, which the sign-out then replaces with a
  // fresh page (lib/sign-out-state.ts).
  if (isSigningOut && !isLoggedIn) {
    return <LoadingScreen />;
  }

  // While the server is asked to end the session, cover the app without
  // unmounting it, so a refused sign-out hands it back exactly as it was.
  return (
    <>
      <SignedInOrOutShell isLoggedIn={isLoggedIn} />
      {isSigningOut
        ? createPortal(
            <div className="fixed inset-0 z-50">
              <LoadingScreen />
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/**
 * A support tab whose session the server no longer answers for (expired,
 * revoked, ended from another tab) ends, rather than offering a sign-in page
 * in the customer's name (ADR-0143).
 */
function useEndSupportTabWithItsSession(isSignedOut: boolean): void {
  useEffect(() => {
    if (SUPPORT_SESSION && isSignedOut) {
      leaveSupportSession();
    }
  }, [isSignedOut]);
}

/** Above every handler Ionic registers (its overlays take 100). */
const HOLD_BACK_BUTTON_PRIORITY = 1000;

/**
 * Nothing reaches the app while a sign-out runs: `#root` goes inert (taps,
 * keys, focus and the screen reader), and the Android back button is held,
 * so nothing is written that the sign-out's clear would discard.
 */
function useHoldAppWhileSigningOut(isSigningOut: boolean): void {
  useEffect(() => {
    const root = document.getElementById("root");
    if (!(isSigningOut && root)) {
      return;
    }
    const holdBackButton = (event: Event) => {
      (event as BackButtonEvent).detail.register(
        HOLD_BACK_BUTTON_PRIORITY,
        () => undefined
      );
    };
    root.setAttribute("inert", "");
    document.addEventListener("ionBackButton", holdBackButton);
    return () => {
      root.removeAttribute("inert");
      document.removeEventListener("ionBackButton", holdBackButton);
    };
  }, [isSigningOut]);
}

function SignedInOrOutShell({ isLoggedIn }: { isLoggedIn: boolean }) {
  // Pre-login (or while session is still resolving): render the router
  // directly so the auth screens mount instantly. AuthGate inside the
  // router handles the actual auth state machine. Uses the same cached-aware
  // session as AuthGate so an offline cold start mounts AuthenticatedShell
  // (and AppProvider) instead of leaving authenticated routes without it.
  if (!isLoggedIn) {
    return <ShellApp />;
  }

  // Post-login: mount the heavy chunk around the same router. The user
  // sees a brief spinner on first sign-in while AuthenticatedShell loads
  // — fine, because PowerSync would need to sync at that point anyway.
  return (
    <Suspense fallback={<LoadingScreen />}>
      <AuthenticatedShell>
        <ShellApp />
      </AuthenticatedShell>
    </Suspense>
  );
}

/** The document title the shell ships with, and the app's own from then on. */
const APP_TITLE = "Vidimo";

/**
 * Hands a prerendered document (ADR-0112) over to the running app.
 *
 * Runs on first commit, so the real React tree is already mounted when it
 * fires — both undos are safe only at that point:
 *
 * - `inert` comes off `#root`. The build marks the prerendered markup inert
 *   because it is readable but not usable: these forms submit through the auth
 *   client, so before React attaches its handlers a submit only reloads the
 *   page and discards what was typed.
 * - The title goes back to the app's. The prerendered `<title>` describes that
 *   one document for a crawler or a link preview; nothing in the SPA manages
 *   `document.title`, so left alone it would follow the reader to every other
 *   route for the rest of the session, and into a bookmark or the installed
 *   PWA's name.
 *
 * A no-op on the shell, which is neither inert nor retitled.
 */
function usePrerenderedDocumentHandover() {
  useEffect(() => {
    document.getElementById("root")?.removeAttribute("inert");
    document.title = APP_TITLE;
  }, []);
}

function App() {
  usePrerenderedDocumentHandover();
  useLaunchSplashRelease();

  // Tell the Capgo native layer that the JS bundle initialised. Without this
  // call Capgo assumes the bundle crashed and silently rolls back on the next
  // launch — see apps/mobile/docs/live-updates.md. Static import + sync call
  // on mount: dynamic import() delays the signal and can trigger rollbacks.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    CapacitorUpdater.notifyAppReady().catch((err) => {
      console.error("[capgo] notifyAppReady failed", err);
    });
  }, []);

  // Log the OTA update lifecycle to the native console on staging/dev builds —
  // silent (no toasts), but the launch status log prints the active channel +
  // bundle, the fastest way to spot a channel mismatch (device polling a
  // channel where no bundles land). Production stays fully quiet.
  useEffect(() => {
    if (!(Capacitor.isNativePlatform() && isDevMenuVisible())) {
      return;
    }
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    void (async () => {
      // Both plugin round-trips start together, but the status read is only
      // diagnostic: it settles on its own so a rejection can never strand the
      // listeners registered below with nothing left to remove them.
      const status = getCapgoStatus().catch((err) => {
        console.error("[capgo] status read failed", err);
        return null;
      });
      const off = await registerCapgoListeners((event) => {
        const message = event.detail
          ? `${event.label}: ${event.detail}`
          : event.label;
        console.log("[capgo]", message);
      });
      if (cancelled) {
        // Torn down before registration resolved: unsubscribe right here, or
        // the listeners outlive the effect with nothing left to remove them.
        off();
        return;
      }
      unsubscribe = off;
      console.log("[capgo] status on launch", await status);
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      disableTransitionOnChange
      enableSystem
    >
      <SystemThemeSync />
      <QueryClientProvider client={queryClient}>
        {/* OUTSIDE <IonApp> on purpose. IonApp sets `contain: size layout
            style` with `z-index: 0`, which makes it a stacking context and
            caps anything inside it — including sonner's z-index. Dialogs
            portal to <body>, outside that context, so a Toaster
            mounted within IonApp paints BEHIND any open dialog and its
            toasts are invisible exactly when a dialog raises them (the
            notification panel's suggestion outcomes, the time-off card). */}
        <Toaster
          closeButton
          // Clear the iOS status bar / Dynamic Island. The phone WebView
          // matches sonner's max-width:600px rule, which reads only the
          // --mobile-offset-* vars, so the top inset must go on mobileOffset
          // (plain `offset` would not move the on-device toast). +12px mirrors
          // the app's safe-area header gap.
          mobileOffset={{ top: "calc(env(safe-area-inset-top) + 12px)" }}
          position="top-center"
          theme="system"
          toastOptions={{
            classNames: {
              // 44pt, overriding sonner's own ~24px: the action on a toast is
              // almost always the way back from something that just happened
              // (Undo on a completed task), so it is the one control a host
              // reaches for in a hurry and cannot afford to miss.
              actionButton: "!h-11 bg-primary px-4 text-primary-foreground",
              description: "text-muted-foreground",
              error: "text-destructive-foreground",
              success: "text-success-foreground",
              toast:
                "bg-card text-foreground border border-border shadow-md rounded-lg",
            },
          }}
        />
        <IonApp className="bg-background">
          <IonReactRouter>
            <PickerSheetProvider>
              <ErrorBoundary>
                <ShellAppBoundary />
              </ErrorBoundary>
            </PickerSheetProvider>
          </IonReactRouter>
        </IonApp>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

async function renderApp(container: HTMLElement): Promise<void> {
  // Seeded before the root exists, so the first frame is already in the
  // language it resolves to.
  seedInterfaceLocale(window.location);
  // Before the first session check, which reads the token by that name.
  await carryNativeSessionTokenOver();
  createRoot(container).render(
    <InterfaceLocaleRoot>
      {window.location.pathname === SUPPORT_SESSION_PATH ? (
        <SupportSessionPage />
      ) : (
        <App />
      )}
    </InterfaceLocaleRoot>
  );
}

// A connect page belongs on the connect origin and everything else on the app
// origin (ADR-0137). Move before rendering: a connect page on the app origin
// would load Meta's SDK beside the app's data, and the app on the connect
// origin would ask the API for a session that origin may not hold.
const misplacedUrl = misplacedPageUrl(window.location);
const root = document.getElementById("root");
if (misplacedUrl) {
  window.location.replace(misplacedUrl);
} else if (root) {
  renderApp(root);
} else {
  console.error("[boot] main.tsx: root element MISSING");
}
